import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../services/api';

// Configurable: change path when Alcazar logo is available
const ALCAZAR_LOGO_PATH = '/xia-logo-mini.svg';

// Clean bot SVG icon (Lucide-style)
const BotIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 8V4H8"/>
    <rect width="16" height="12" x="4" y="8" rx="2"/>
    <path d="M2 14h2"/>
    <path d="M20 14h2"/>
    <path d="M15 13v2"/>
    <path d="M9 13v2"/>
  </svg>
);


// WhatsApp floating icon
const WhatsAppIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#fff">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const AlcazarTestChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [agentOnline, setAgentOnline] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactReady, setContactReady] = useState(false);
  const [chatReady, setChatReady] = useState(false);
  const [waitingGreeting, setWaitingGreeting] = useState(false);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  // Check if agent is online (supervised mode)
  useEffect(() => {
    const checkAgent = () => {
      api.get('/api/alcazar/agente/status').then(res => {
        setAgentOnline(res.data?.supervised || false);
      }).catch(() => setAgentOnline(false));
    };
    checkAgent();
    const interval = setInterval(checkAgent, 10000);
    return () => clearInterval(interval);
  }, []);

  // After contact submitted: show wait message, then greeting after 60s
  useEffect(() => {
    if (contactReady && !chatReady) {
      setWaitingGreeting(true);
      setMessages([{ role: 'xia', text: 'Espere a un agente operativo...', ts: Date.now() }]);
      const timer = setTimeout(() => {
        setMessages(prev => [...prev, { role: 'xia', text: 'Hola, \u00bfen qu\u00e9 puedo ayudarte sobre el Real Alc\u00e1zar de Sevilla?', ts: Date.now() }]);
        setWaitingGreeting(false);
        setChatReady(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }, 60000);
      return () => clearTimeout(timer);
    }
  }, [contactReady, chatReady]);

  const scrollToBottom = useCallback(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const openChat = () => {
    setIsOpen(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setVisible(true));
    });
    setTimeout(() => inputRef.current?.focus(), 200);
  };

  const closeChat = () => {
    setVisible(false);
    setTimeout(() => setIsOpen(false), 200);
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading || waitingGreeting) return;
    if (trimmed.length > 2000) return;

    setMessages(prev => [...prev, { role: 'user', text: trimmed, ts: Date.now() }]);
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 50);
    setLoading(true);

    try {
      // Call RAG v4 directly via backend proxy
      const res = await api.post('/api/alcazar/dashboard/chat', { message: trimmed });
      const d = res.data;
      // If supervised mode blocked the response, call RAG directly
      if (!d.response || d.action === 'SUPERVISED_HOLD') {
        const ragRes = await fetch('/api/alcazar/v4-stats');  // dummy to check auth
        // Fallback: show what we got
        setMessages(prev => [...prev, {
          role: 'xia',
          text: d.response || 'Tu mensaje ha sido recibido. Un agente lo revisará en breve.',
          action: d.action || 'SUPERVISED',
          ts: Date.now()
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'xia',
          text: d.response || '(sin respuesta)',
          action: d.action,
          confidence: d.confidence,
          debug: d.debug,
          request_id: d.request_id,
          ts: Date.now()
        }]);
      }
    } catch (err) {
      const s = err.response?.status;
      let t = 'Error al contactar el pipeline';
      if (s === 429) t = 'Rate limit: max 10 mensajes/minuto';
      else if (s === 400) t = err.response?.data?.error || 'Input invalido';
      else if (err.response?.data?.detail) t += ': ' + err.response.data.detail;
      setMessages(prev => [...prev, { role: 'error', text: t, ts: Date.now() }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const copyDebug = (debug, rid) => {
    navigator.clipboard.writeText(JSON.stringify({ request_id: rid, ...debug }, null, 2));
    setCopyFeedback(rid);
    setTimeout(() => setCopyFeedback(null), 1500);
  };

  const fmtMs = (ms) => !ms ? '-' : ms < 1000 ? ms + ' ms' : (ms / 1000).toFixed(1) + ' s';

  // Small bot avatar for chat bubbles
  const BotAvatar = () => (
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      background: '#4f46e5', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <BotIcon size={15} color="#fff" />
    </div>
  );

  // Only show chat when agent is online (PLAY active)
  if (!agentOnline && !isOpen) return (
    <>
      {/* WhatsApp always visible */}
      <a href="https://wa.me/34617584192" target="_blank" rel="noopener noreferrer"
        style={{ position: 'fixed', right: 24, bottom: 24, width: 54, height: 54, borderRadius: 999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#25D366', color: '#fff', cursor: 'pointer',
          boxShadow: '0 8px 20px rgba(0,0,0,.15)', zIndex: 9999, textDecoration: 'none' }}
        title="Enviar WhatsApp">
        <WhatsAppIcon size={28} />
      </a>
    </>
  );

  return (
    <>

      {/* ---- WhatsApp FAB Button ---- */}
      {!isOpen && (
        <a
          href="https://wa.me/34617584192"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            position: 'fixed', right: 24, bottom: 96,
            width: 54, height: 54, borderRadius: 999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#25D366', color: '#fff', cursor: 'pointer',
            boxShadow: '0 8px 20px rgba(0,0,0,.15)',
            userSelect: 'none', zIndex: 9999,
            textDecoration: 'none',
            transition: 'transform .15s ease',
          }}
          title="Enviar WhatsApp"
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <WhatsAppIcon size={28} />
        </a>
      )}

      {/* ---- FAB Button ---- */}
      {!isOpen && (
        <div
          onClick={openChat}
          style={{
            position: 'fixed', right: 24, bottom: 24,
            width: 62, height: 62, borderRadius: 999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#4f46e5', color: '#fff', cursor: 'pointer',
            boxShadow: '0 10px 25px rgba(0,0,0,.18)',
            userSelect: 'none', zIndex: 9999,
          }}
          title="Chat de pruebas XIA"
        >
          <BotIcon size={28} color="#fff" />
          {/* Badge TEST */}
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: '#eab308', color: '#713f12',
            fontSize: 9, fontWeight: 800, padding: '2px 6px',
            borderRadius: 6, lineHeight: 1,
          }}>TEST</span>
        </div>
      )}

      {/* ---- Contact Form (before chat opens) ---- */}
      {isOpen && !contactReady && (
        <div style={{
          position: 'fixed', right: 24, bottom: 96,
          width: 360, borderRadius: 18, background: '#fff',
          boxShadow: '0 20px 60px rgba(0,0,0,.2)',
          zIndex: 10000, overflow: 'hidden',
        }}>
          <div style={{ background: '#4f46e5', color: '#fff', padding: '20px 24px' }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Real Alcazar de Sevilla</div>
            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>Introduce tu email para iniciar el chat</div>
          </div>
          <div style={{ padding: 24 }} onKeyDown={e => e.stopPropagation()}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Email *</label>
            <input
              type="email"
              value={contactEmail}
              onChange={e => setContactEmail(e.target.value)}
              placeholder="tu@email.com"
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 12, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = '#4f46e5'}
              onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter' && contactEmail.includes('@')) setContactReady(true); }}
              autoFocus
            />
            <button
              onClick={() => { if (contactEmail.includes('@')) setContactReady(true); }}
              disabled={!contactEmail.includes('@')}
              style={{
                width: '100%', marginTop: 16, padding: '12px 0', background: contactEmail.includes('@') ? '#4f46e5' : '#d1d5db',
                color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: contactEmail.includes('@') ? 'pointer' : 'default',
              }}
            >Iniciar chat</button>
            <button onClick={closeChat} style={{ width: '100%', marginTop: 8, padding: 8, background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
          </div>
        </div>
      )}

      {/* ---- Panel ---- */}
      {isOpen && contactReady && (
        <div style={{
          position: 'fixed', right: 24, bottom: 96,
          width: 390, height: 610, maxHeight: '80vh',
          borderRadius: 18, background: '#fff',
          boxShadow: '0 20px 45px rgba(0,0,0,.22)',
          overflow: 'hidden', zIndex: 9999,
          border: '1px solid rgba(0,0,0,.06)',
          display: 'flex', flexDirection: 'column',
          transform: visible ? 'translateY(0)' : 'translateY(12px)',
          opacity: visible ? 1 : 0,
          transition: 'all .2s ease',
        }}>

          {/* Header */}
          <div style={{
            height: 64, background: '#4f46e5', color: '#fff',
            display: 'flex', alignItems: 'center', padding: '0 12px', gap: 10,
            flexShrink: 0,
          }}>
            {/* Bot avatar */}
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(255,255,255,.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <BotIcon size={22} color="#fff" />
            </div>

            {/* Title */}
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, flex: 1 }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>XIA &ndash; Chat de Pruebas</span>
              <span style={{
                fontSize: 9, fontWeight: 700, color: '#fbbf24',
                letterSpacing: '0.06em', marginTop: 2,
              }}>MODO PRUEBAS</span>
            </div>

            {/* Alcazar logo (sello) */}
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(255,255,255,.15)',
              border: '1.5px solid rgba(255,255,255,.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', flexShrink: 0,
            }}>
              <img
                src={ALCAZAR_LOGO_PATH}
                alt="Logo"
                style={{ width: 22, height: 22, objectFit: 'contain' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>

            {/* Debug toggle */}
            <div
              onClick={() => setShowDebug(!showDebug)}
              style={{
                padding: '4px 8px', borderRadius: 8, cursor: 'pointer',
                fontSize: 10, fontWeight: 600,
                background: showDebug ? 'rgba(255,255,255,.25)' : 'rgba(255,255,255,.1)',
                color: showDebug ? '#fff' : 'rgba(255,255,255,.55)',
                transition: 'all .15s', whiteSpace: 'nowrap',
              }}
            >
              Debug {showDebug ? 'ON' : 'OFF'}
            </div>

            {/* Close */}
            <div
              onClick={closeChat}
              style={{
                width: 32, height: 32, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: 16,
                color: 'rgba(255,255,255,.65)',
                background: 'rgba(255,255,255,.08)',
                flexShrink: 0,
              }}
              title="Minimizar"
            >
              &#x2715;
            </div>
          </div>

          {/* Body */}
          <div ref={bodyRef} style={{
            flex: 1, overflowY: 'auto', padding: 14,
            background: '#f6f7fb', minHeight: 0,
          }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: 50, fontSize: 13 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: '#e0e7ff', margin: '0 auto 12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <BotIcon size={30} color="#6366f1" />
                </div>
                <div style={{ fontWeight: 600, color: '#6b7280' }}>Chat de pruebas aislado</div>
                <div style={{ fontSize: 11, marginTop: 4, color: '#b0b0b0' }}>No genera emails &middot; No afecta metricas &middot; No Lambda</div>
                <div style={{ fontSize: 11, marginTop: 2, color: '#b0b0b0' }}>Enter para enviar &middot; Shift+Enter salto de linea</div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div key={idx} style={{ marginBottom: showDebug && msg.debug ? 4 : 10 }}>
                {/* Message row with avatar */}
                <div style={{
                  display: 'flex',
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'flex-end',
                  gap: 8,
                }}>
                  {/* Avatar (only for XIA/error) */}
                  {msg.role !== 'user' && <BotAvatar />}

                  {/* Bubble */}
                  <div style={{
                    maxWidth: '78%',
                    padding: '10px 14px',
                    borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    fontSize: 14,
                    lineHeight: 1.45,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    ...(msg.role === 'user' ? {
                      background: '#e5e7eb', color: '#1f2937',
                    } : msg.role === 'error' ? {
                      background: '#fef2f2', color: '#b91c1c',
                      border: '1px solid #fecaca',
                    } : {
                      background: '#fff',
                      border: '1px solid rgba(0,0,0,.06)', color: '#1f2937',
                    }),
                  }}>
                    {msg.text}
                    {msg.action && (
                      <div style={{ marginTop: 6, fontSize: 11, fontFamily: 'monospace',
                        color: msg.action === 'SEND_DRAFT' ? '#16a34a' :
                               msg.action.startsWith('HANDOFF') ? '#d97706' :
                               msg.action === 'PEDIR_INTENCION' ? '#6b7280' : '#4f46e5'
                      }}>
                        {msg.action === 'SEND_DRAFT' ? 'Respuesta automatica' :
                         msg.action === 'HANDOFF_ENTRADAS' ? 'Derivado a equipo' :
                         msg.action === 'SEND_PATRONATO' ? 'Derivado a Patronato' :
                         msg.action === 'PEDIR_INTENCION' ? 'Esperando mas datos' :
                         msg.action}
                        {msg.confidence ? ` | ${(msg.confidence * 100).toFixed(0)}%` : ''}
                      </div>
                    )}
                  </div>
                </div>

                {/* Debug panel */}
                {showDebug && msg.debug && (
                  <div style={{
                    maxWidth: '85%', marginTop: 4, marginBottom: 8,
                    marginLeft: 36,
                    background: '#f0f0f5', border: '1px solid #e2e2ea',
                    borderRadius: 10, padding: '8px 10px',
                    fontSize: 11, fontFamily: 'monospace', lineHeight: 1.6,
                    color: '#374151',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ color: '#9ca3af', fontWeight: 600, fontSize: 10, textTransform: 'uppercase' }}>Debug</span>
                      <span
                        onClick={() => copyDebug(msg.debug, msg.request_id)}
                        style={{ color: '#4f46e5', cursor: 'pointer', fontSize: 10, fontWeight: 600 }}
                      >
                        {copyFeedback === msg.request_id ? 'Copiado!' : 'Copiar JSON'}
                      </span>
                    </div>
                    <div><span style={{ color: '#9ca3af' }}>Lang:</span> {msg.debug.language_detected || '-'}
                      {msg.debug.translated && <span style={{ color: '#ea580c', marginLeft: 4 }}>&rarr; {msg.debug.target_lang}</span>}
                    </div>
                    <div><span style={{ color: '#9ca3af' }}>Intent:</span>{' '}
                      <span style={{ color: '#4f46e5', fontWeight: 700 }}>{msg.debug.intent}</span>
                    </div>
                    {msg.debug.all_intents?.length > 1 && (
                      <div><span style={{ color: '#9ca3af' }}>All:</span> {msg.debug.all_intents.join(', ')}</div>
                    )}
                    {msg.debug.flags?.length > 0 && (
                      <div><span style={{ color: '#9ca3af' }}>Flags:</span>{' '}
                        <span style={{ color: '#ca8a04' }}>{msg.debug.flags.join(', ')}</span>
                      </div>
                    )}
                    {msg.debug.suppressed?.length > 0 && (
                      <div><span style={{ color: '#9ca3af' }}>Suppressed:</span>{' '}
                        <span style={{ color: '#dc2626' }}>{msg.debug.suppressed.join(', ')}</span>
                      </div>
                    )}
                    {msg.debug.resolved?.length > 0 && (
                      <div><span style={{ color: '#9ca3af' }}>Resolved:</span> {msg.debug.resolved.map(r => r.intent).join(', ')}</div>
                    )}
                    {msg.debug.unresolved?.length > 0 && (
                      <div><span style={{ color: '#9ca3af' }}>Unresolved:</span>{' '}
                        <span style={{ color: '#ea580c' }}>{msg.debug.unresolved.map(u => u.intent).join(', ')}</span>
                      </div>
                    )}
                    <div><span style={{ color: '#9ca3af' }}>Time:</span> {fmtMs(msg.debug.response_time_ms)}
                      {msg.debug.stages && (
                        <span style={{ color: '#b0b0b0', marginLeft: 6 }}>
                          ({msg.debug.stages.map(s => {
                            const n = s.name.split('_').pop();
                            return n.slice(0, 5) + ':' + (s.ms || 0);
                          }).join(' ')})
                        </span>
                      )}
                    </div>
                    <div style={{ color: '#9ca3af', fontSize: 9, marginTop: 2 }}>
                      ID: {msg.request_id}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                <BotAvatar />
                <div style={{
                  padding: '10px 14px', borderRadius: '14px 14px 14px 4px',
                  background: '#fff', border: '1px solid rgba(0,0,0,.06)',
                  fontSize: 13, color: '#6366f1',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{
                    width: 14, height: 14, border: '2px solid #a5b4fc',
                    borderTopColor: 'transparent', borderRadius: '50%',
                    animation: 'xia-spin .6s linear infinite', display: 'inline-block',
                  }}/>
                  Procesando...
                  <style>{`@keyframes xia-spin{to{transform:rotate(360deg)}}`}</style>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: 12, borderTop: '1px solid rgba(0,0,0,.06)',
            background: '#fff', display: 'flex', gap: 10,
            alignItems: 'flex-end', flexShrink: 0,
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu mensaje..."
              disabled={loading}
              maxLength={2000}
              rows={1}
              style={{
                flex: 1, resize: 'none', borderRadius: 12,
                padding: '10px 12px', border: '1px solid rgba(0,0,0,.12)',
                outline: 'none', fontSize: 14, maxHeight: 110,
                fontFamily: 'inherit',
              }}
              onFocus={(e) => { e.target.style.borderColor = '#818cf8'; e.target.style.boxShadow = '0 0 0 2px rgba(99,102,241,.15)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'rgba(0,0,0,.12)'; e.target.style.boxShadow = 'none'; }}
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              style={{
                height: 42, padding: '0 18px', borderRadius: 12,
                border: 0, background: loading || !input.trim() ? '#c7d2fe' : '#4f46e5',
                color: '#fff', cursor: loading || !input.trim() ? 'default' : 'pointer',
                fontWeight: 600, fontSize: 14,
                transition: 'background .15s',
              }}
            >
              Enviar
            </button>
          </div>

          {/* Clear button */}
          {messages.length > 0 && (
            <div style={{
              padding: '6px 12px', borderTop: '1px solid rgba(0,0,0,.04)',
              background: '#fafafa', textAlign: 'center', flexShrink: 0,
            }}>
              <span
                onClick={() => setMessages([])}
                style={{ fontSize: 11, color: '#9ca3af', cursor: 'pointer' }}
              >
                Limpiar conversacion
              </span>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AlcazarTestChat;
